import React, { useState, useEffect } from 'react';
import LiveStreamCarousel from '../components/LiveStreamCarousel';
import { apiService } from '../services/api';

const Home = () => {
  const [statistics, setStatistics] = useState({
    teamMembers: '13+',
    hoursStreamed: '22,000+',
    charityRaised: '$5K+',
    communityEvents: 'Monthly'
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStatistics();
  }, []);

  const fetchStatistics = async () => {
    try {
      const response = await apiService.getStatisticsData();
      if (response.data && response.data.homepage) {
        setStatistics(response.data.homepage);
      }
    } catch (error) {
      console.error('Error fetching statistics:', error);
      // Keep default values if fetch fails
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{minHeight: '100vh'}}>
      {/* Hero Section */}
      <section className="hero">
        <div className="container">
          {/* Logo */}
          <div style={{display: 'flex', justifyContent: 'center', marginBottom: '2rem'}}>
            <img
              src="/TCST_WideLogo_Lowres.png"
              alt="The Canopy Stream Team"
              style={{height: '6rem', animation: 'fadeIn 0.5s ease-in-out'}}
              onError={(e) => {
                e.target.src = "/TCST_RoundLogo_Lowres.png.png";
              }}
            />
          </div>

          {/* Tagline */}
          <h1 className="text-gradient fade-in">
            Creativity • Community • Collaboration • Care
          </h1>

          {/* Mission Statement */}
          <p className="fade-in">
            Welcome to The Canopy Stream Team - a diverse community of creators who believe in 
            supporting each other, entertaining audiences, and making a positive impact through 
            streaming and charitable endeavors.
          </p>

          {/* CTA Buttons */}
          <div className="hero-buttons fade-in">
            <a href="/team" className="btn btn-primary">
              Meet the Team
            </a>
            <a href="/events" className="btn btn-secondary">
              Upcoming Events
            </a>
            <a href="/charity" className="btn btn-outline">
              Support a Cause
            </a>
          </div>
        </div>
      </section>

      {/* Live Streams Section */}
      <section className="section section-bg">
        <div className="container">
          <LiveStreamCarousel />
        </div>
      </section>

      {/* Team Highlights */}
      <section className="section">
        <div className="container">
          <div className="grid grid-3">
            {/* Featured Member */}
            <div className="card">
              <div className="text-center">
                <div style={{
                  width: '4rem', 
                  height: '4rem', 
                  backgroundColor: 'var(--forest-200)', 
                  borderRadius: '50%', 
                  margin: '0 auto 1rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '2rem'
                }}>
                  👥
                </div>
                <h3 style={{fontSize: '1.25rem', fontWeight: 'bold', color: 'var(--forest-800)', marginBottom: '0.5rem'}}>
                  Diverse Community
                </h3>
                <p style={{color: 'var(--forest-600)'}}>
                  Our team includes streamers from various backgrounds, all united by passion for 
                  gaming, creativity, and community building.
                </p>
              </div>
            </div>

            {/* Featured Charity */}
            <div className="card">
              <div className="text-center">
                <div style={{
                  width: '4rem', 
                  height: '4rem', 
                  backgroundColor: 'var(--gold-200)', 
                  borderRadius: '50%', 
                  margin: '0 auto 1rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '2rem'
                }}>
                  💛
                </div>
                <h3 style={{fontSize: '1.25rem', fontWeight: 'bold', color: 'var(--forest-800)', marginBottom: '0.5rem'}}>
                  Charitable Impact
                </h3>
                <p style={{color: 'var(--forest-600)'}}>
                  We regularly organize charity streams and events to support causes we care about, 
                  making a positive difference in the world.
                </p>
              </div>
            </div>

            {/* Community Events */}
            <div className="card">
              <div className="text-center">
                <div style={{
                  width: '4rem', 
                  height: '4rem', 
                  backgroundColor: 'var(--earth-200)', 
                  borderRadius: '50%', 
                  margin: '0 auto 1rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '2rem'
                }}>
                  🎮
                </div>
                <h3 style={{fontSize: '1.25rem', fontWeight: 'bold', color: 'var(--forest-800)', marginBottom: '0.5rem'}}>
                  Community Events
                </h3>
                <p style={{color: 'var(--forest-600)'}}>
                  From raid trains to game nights, we host regular events that bring our 
                  community together for fun and memorable experiences.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Stats */}
      <section className="section section-bg">
        <div className="container" style={{maxWidth: '64rem'}}>
          <h2 className="text-center text-3xl font-bold text-gradient mb-12">
            The Canopy by the Numbers
          </h2>
          <div className="grid grid-4 text-center">
            <div className="fade-in">
              <div style={{fontSize: '2.25rem', fontWeight: 'bold', color: 'var(--forest-600)', marginBottom: '0.5rem'}}>
                {loading ? '...' : statistics.teamMembers}
              </div>
              <div style={{color: 'var(--forest-800)', fontWeight: '500'}}>Team Members</div>
            </div>
            <div className="fade-in" style={{animationDelay: '0.5s'}}>
              <div style={{fontSize: '2.25rem', fontWeight: 'bold', color: 'var(--earth-600)', marginBottom: '0.5rem'}}>
                {loading ? '...' : statistics.hoursStreamed}
              </div>
              <div style={{color: 'var(--forest-800)', fontWeight: '500'}}>Hours Streamed</div>
            </div>
            <div className="fade-in" style={{animationDelay: '1s'}}>
              <div style={{fontSize: '2.25rem', fontWeight: 'bold', color: 'var(--gold-600)', marginBottom: '0.5rem'}}>
                {loading ? '...' : statistics.charityRaised}
              </div>
              <div style={{color: 'var(--forest-800)', fontWeight: '500'}}>Raised for Charity</div>
            </div>
            <div className="fade-in" style={{animationDelay: '1.5s'}}>
              <div style={{fontSize: '2.25rem', fontWeight: 'bold', color: 'var(--forest-600)', marginBottom: '0.5rem'}}>
                {loading ? '...' : statistics.communityEvents}
              </div>
              <div style={{color: 'var(--forest-800)', fontWeight: '500'}}>Community Events</div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="section">
        <div className="container" style={{maxWidth: '64rem'}}>
          <div className="card text-center">
            <h2 className="text-3xl font-bold text-gradient mb-6">
              Join The Canopy Community
            </h2>
            <p style={{color: 'var(--forest-600)', marginBottom: '2rem', fontSize: '1.125rem'}}>
              Whether you're a content creator, gaming enthusiast, or just love good vibes, 
              you're welcome under our canopy. Follow us on social media and join our 
              streams to become part of our growing family.
            </p>
            <div style={{display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap'}}>
              <a 
                href="https://discord.gg/EpT75sf4CK" 
                target="_blank" 
                rel="noopener noreferrer"
                className="btn btn-primary"
              >
                Join Discord
              </a>
              <a href="/socials" className="btn btn-outline">
                Follow Our Socials
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;